import { parseDiscountCodeParam, parseEntryParams } from '../../features/editor/entry-params';
import { withoutQueryParam } from './gift-card-link';

/**
 * A discount code handed to the funnel in its entry link, held until the
 * reservation it is meant for exists.
 *
 * Campaign pages and the legacy webshop link customers in as
 * `/?trip=555&departureDate=2026-11-23&discountCode=SYKSY`, so the code the
 * campaign advertises is on the booking without the customer typing it.
 *
 * Unlike a gift-card code (`gift-card-link.ts`) a discount code is no secret —
 * the campaign prints it — so it does not have to leave the address bar before
 * Sentry starts. It is still taken out, and held here instead: left in the URL,
 * the landing → editor hand-off and every reload would carry it on, and a code
 * the customer removed would come back on the next page load. Held in
 * `sessionStorage` so a transient failure can retry on a later page load, and
 * scoped to the trip the link was for. Applying it is `DiscountCodeLinkService`'s
 * job.
 */

const STORAGE_KEY = 'imtFunnelDiscountCodeLink';
const PARAM = 'discountCode';

export interface PendingDiscountCodeLink {
  /** Normalised, see {@link parseDiscountCodeParam}. */
  code: string;
  /** The trip the link was for. */
  tripId: number;
}

/** Where the code is held when storage is blocked (private mode, cookies off):
 *  for this page load only, which still covers the landing → editor hand-off. */
let heldInMemory: PendingDiscountCodeLink | null = null;

/**
 * Take a discount code out of the entry URL and hold on to it. Called once at
 * bootstrap, before the router copies the URL's params into its navigations.
 *
 * A code the funnel cannot use — blank, too long, or on a link that names no
 * trip — is removed all the same, and not kept; nor is one held from an earlier
 * link, since this link is the latest word on which code the customer means.
 * Only a URL with no `discountCode` at all leaves a held code alone.
 *
 * Best effort on the URL: when the browser refuses the rewrite (a sandboxed
 * frame can), the code stays in the address bar, which is harmless for a code
 * that is no secret.
 */
export function captureDiscountCodeLink(): void {
  const { pathname, search, hash } = window.location;
  const remaining = withoutQueryParam(search, PARAM);
  if (remaining === null) return;

  const entry = parseEntryParams(new URLSearchParams(search));
  if (entry.discountCode !== null && entry.tripId !== null) {
    storePendingDiscountCodeLink({ code: entry.discountCode, tripId: entry.tripId });
  } else {
    clearPendingDiscountCodeLink();
  }

  try {
    window.history.replaceState(window.history.state, '', `${pathname}${remaining}${hash}`);
  } catch {
    // Left in the address bar; see above.
  }
}

/** Hold `link` until {@link clearPendingDiscountCodeLink}, replacing any earlier one. */
export function storePendingDiscountCodeLink(link: PendingDiscountCodeLink): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(link));
    heldInMemory = null;
  } catch {
    heldInMemory = link;
  }
}

/** The held code, or null. Re-validated on the way out: `sessionStorage`
 *  outlives a deploy. */
export function readPendingDiscountCodeLink(): PendingDiscountCodeLink | null {
  if (heldInMemory !== null) return heldInMemory;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw === null ? null : parseStored(raw);
  } catch {
    return null;
  }
}

export function clearPendingDiscountCodeLink(): void {
  heldInMemory = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Blocked storage: nothing was written there to clear.
  }
}

function parseStored(raw: string): PendingDiscountCodeLink | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (value === null || typeof value !== 'object') return null;
  const { code, tripId } = value as Record<string, unknown>;
  const normalised = typeof code === 'string' ? parseDiscountCodeParam(code) : null;
  if (normalised === null || typeof tripId !== 'number' || !Number.isInteger(tripId)) return null;
  return tripId > 0 ? { code: normalised, tripId } : null;
}
